// Where a user's storage goes (ADR 0035): the current files / older
// versions / trash split, per-folder sizes, and the files whose old
// versions use the most space. Every file change can move these
// numbers, so useInvalidateFiles() refreshes them too.

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import type {
  StorageFolderResponse,
  StorageSummary,
  VersionHeavyFile,
} from "./types";
import { errorDetail } from "@/lib/apiError";

export const storageKeys = {
  all: ["storage"] as const,
  summary: () => [...storageKeys.all, "summary"] as const,
  folder: (folderId: string | null) =>
    [...storageKeys.all, "folder", folderId] as const,
  versions: () => [...storageKeys.all, "versions"] as const,
};

export function useStorageSummary() {
  return useQuery({
    queryKey: storageKeys.summary(),
    queryFn: async (): Promise<StorageSummary> => {
      const { data, error } = await api.GET("/api/v1/storage", {});
      if (error || !data)
        throw new Error(errorDetail(error, "could not load storage usage"));
      return data;
    },
  });
}

export function useStorageFolder(folderId: string | null) {
  return useQuery({
    queryKey: storageKeys.folder(folderId),
    queryFn: async (): Promise<StorageFolderResponse> => {
      const { data, error } = await api.GET("/api/v1/storage/folders", {
        params: { query: folderId ? { folder_id: folderId } : {} },
      });
      if (error || !data)
        throw new Error(errorDetail(error, "could not load folder sizes"));
      return data;
    },
  });
}

export function useVersionHeavyFiles() {
  return useQuery({
    queryKey: storageKeys.versions(),
    queryFn: async (): Promise<VersionHeavyFile[]> => {
      const { data, error } = await api.GET("/api/v1/storage/versions", {});
      if (error || !data)
        throw new Error(errorDetail(error, "could not load versions"));
      return data.items;
    },
  });
}

/** Delete every older version of a file; kept ones only if asked. */
export async function deleteOldVersions(
  fileId: string,
  includeKept: boolean,
): Promise<{ deleted: number; freed_bytes: number }> {
  const { data, error } = await api.POST(
    "/api/v1/files/{file_id}/versions/delete-old",
    {
      params: { path: { file_id: fileId } },
      body: { include_kept: includeKept },
    },
  );
  if (error || !data)
    throw new Error(errorDetail(error, "deleting old versions failed"));
  return data;
}

/** Permanently delete one older version. */
export async function deleteVersion(
  fileId: string,
  versionId: string,
): Promise<void> {
  const { error, response } = await api.DELETE(
    "/api/v1/files/{file_id}/versions/{version_id}",
    { params: { path: { file_id: fileId, version_id: versionId } } },
  );
  if (!response.ok) {
    throw new Error(errorDetail(error, `HTTP ${response.status}`));
  }
}
